import { useCallback } from 'react';
import { v5 as uuidv5 } from 'uuid';

const STORAGE_NAMESPACE = '123e4567-e89b-12d3-a456-426614174000';

export const generateStorageKey = (userId: string, key: string) => `${uuidv5(userId, STORAGE_NAMESPACE)}.${key}`;

export const getStorage = (storageType: 'local' | 'session') => {
    const storage = storageType === 'local' ? localStorage : sessionStorage;
    const setItem = <T>(key: string, value: T) => {
        try {
            const stringifiedValue = JSON.stringify(value);
            storage.setItem(key, stringifiedValue);
        } catch (e) {
            console.error(`Error serializing value for key: ${key}`, e);
        }
    };

    const getItem = <T>(key: string, defaultValue: T): T => {
        const storedValue = storage.getItem(key);
        if (storedValue !== null) {
            try {
                return JSON.parse(storedValue) as T;
            } catch (e) {
                console.warn(`Error parsing storage value for key: ${key}`, e);
            }
        }

        return defaultValue;
    };

    const removeItem = (key: string) => {
        storage.removeItem(key);
    };

    return {
        setItem,
        getItem,
        removeItem,
    };
};

export const useStorage = (storageType: 'local' | 'session', userId: string) => {
    const storage = storageType === 'local' ? localStorage : sessionStorage;

    const specifiedSetItem = useCallback(<T>(key: string, value: T) => {
        const storageKey = generateStorageKey(userId, key);

        try {
            const stringifiedValue = JSON.stringify(value);
            storage.setItem(storageKey, stringifiedValue);
        } catch (e) {
            console.error(`Error serializing value for key: ${storageKey}`, e);
        }
    }, [storage, userId]);

    const specifiedGetItem = useCallback(<T>(key: string, defaultValue: T): T => {
        const storageKey = generateStorageKey(userId, key);
        const storedValue = storage.getItem(storageKey);

        if (storedValue !== null) {
            try {
                return JSON.parse(storedValue) as T;
            } catch (e) {
                console.warn(`Error parsing storage value for key: ${storageKey}`, e);
            }
        }

        return defaultValue;
    }, [storage, userId]);

    const specifiedRemoveItem = useCallback((key: string) => {
        if (!userId) {
            throw new Error('User is not initialized just yet.');
        }

        const storageKey = generateStorageKey(userId, key);
        storage.removeItem(storageKey);
    }, [storage, userId]);

    return {
        specifiedSetItem,
        specifiedGetItem,
        specifiedRemoveItem,
    };
};
